import { Box, Typography, Paper, Grid, Alert, LinearProgress } from '@mui/material';
import { AttachMoney, Receipt } from '@mui/icons-material';
import { DRecord, StatCard, SectionHeader } from './DashboardCard';

export const StatsRow = ({ activeCount, onboardingCount, pendingActions, disciplinaryCount, terminationsPendingCount, expiringCerts, trainingCost, trainingUtilization }: { activeCount: number; onboardingCount: number; pendingActions: number; disciplinaryCount: number; terminationsPendingCount: number; expiringCerts: number; trainingCost: number; trainingUtilization: number }) => (
  <Grid container spacing={2.5} sx={{ mb: 3 }}>
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <StatCard title="Active Employees" value={activeCount} subtitle={`${onboardingCount} in onboarding`} trend="up" />
    </Grid>
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <Paper sx={{ p: 2.5, height: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Pending Actions</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: pendingActions > 0 ? 'warning.main' : 'text.primary' }}>{pendingActions}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {disciplinaryCount} disciplinary, {terminationsPendingCount} terminations
        </Typography>
      </Paper>
    </Grid>
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <Paper sx={{ p: 2.5, height: '100%', border: expiringCerts > 0 ? '2px solid' : 'none', borderColor: 'error.main' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>OHS Alerts</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: expiringCerts > 0 ? 'error.main' : 'text.primary' }}>{expiringCerts}</Typography>
        </Box>
        <Typography variant="caption" color={expiringCerts > 0 ? 'error.main' : 'text.secondary'}>
          {expiringCerts > 0 ? 'certifications expiring soon' : 'all current'}
        </Typography>
      </Paper>
    </Grid>
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <Paper sx={{ p: 2.5, height: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Training Budget</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: trainingUtilization > 90 ? 'error.main' : trainingUtilization > 75 ? 'warning.main' : 'success.main' }}>
          R {trainingCost.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress variant="determinate" value={Math.min(trainingUtilization, 100)} sx={{ flex: 1, height: 6, borderRadius: 1 }} />
          <Typography variant="caption">{trainingUtilization}%</Typography>
        </Box>
      </Paper>
    </Grid>
  </Grid>
);

export const FinanceOverview = ({ totalInvoiced, totalReceived, invoiceCount, receiptCount, overdueInvoices }: { totalInvoiced: number; totalReceived: number; invoiceCount: number; receiptCount: number; overdueInvoices: number }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Finance Overview" />
    <Grid container spacing={2}>
      <Grid size={{ xs: 6 }}>
        <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AttachMoney sx={{ color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">Total Invoiced</Typography>
          </Box>
          <Typography variant="h5" fontWeight={700}>R {totalInvoiced.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}</Typography>
          <Typography variant="caption" color="text.secondary">{invoiceCount} invoices</Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Receipt sx={{ color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">Total Received</Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} color="success.main">R {totalReceived.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}</Typography>
          <Typography variant="caption" color="text.secondary">{receiptCount} receipts</Typography>
        </Paper>
      </Grid>
    </Grid>
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">Collection Rate</Typography>
        <Typography variant="body2" fontWeight={600}>
          {totalInvoiced > 0 ? Math.round((totalReceived / totalInvoiced) * 100) : 0}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={totalInvoiced > 0 ? Math.min((totalReceived / totalInvoiced) * 100, 100) : 0}
        sx={{ height: 8, borderRadius: 1 }}
        color={totalReceived / totalInvoiced > 0.8 ? 'success' : totalReceived / totalInvoiced > 0.5 ? 'warning' : 'error'}
      />
    </Box>
    {overdueInvoices > 0 && (
      <Alert severity="warning" sx={{ mt: 2 }}>
        {overdueInvoices} overdue invoice{overdueInvoices > 1 ? 's' : ''} requiring attention
      </Alert>
    )}
  </Paper>
);

export const SkillsDevelopment = ({ trainingRecords, trainingBudget, trainingCost, trainingUtilization }: { trainingRecords: DRecord[]; trainingBudget: number; trainingCost: number; trainingUtilization: number }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Skills Development" actionHref="/training-records" actionLabel="View All" />
    <Grid container spacing={2}>
      <Grid size={{ xs: 4 }}>
        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
          <Typography variant="h4" fontWeight={700} color="primary.dark">{trainingRecords?.filter((t: DRecord) => t.status === 'Completed').length ?? 0}</Typography>
          <Typography variant="caption">Completed</Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
          <Typography variant="h4" fontWeight={700} color="info.dark">{trainingRecords?.filter((t: DRecord) => t.status === 'InProgress').length ?? 0}</Typography>
          <Typography variant="caption">In Progress</Typography>
        </Paper>
      </Grid>
      <Grid size={{ xs: 4 }}>
        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
          <Typography variant="h4" fontWeight={700}>{trainingRecords?.filter((t: DRecord) => t.status === 'Planned').length ?? 0}</Typography>
          <Typography variant="caption">Planned</Typography>
        </Paper>
      </Grid>
    </Grid>
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">Annual Training Budget</Typography>
        <Typography variant="body2" fontWeight={600}>R {(trainingBudget / 100).toLocaleString()}</Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">Spent</Typography>
        <Typography variant="body2" fontWeight={600} color={trainingUtilization > 90 ? 'error.main' : 'text.primary'}>R {trainingCost.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(trainingUtilization, 100)}
        sx={{ height: 8, borderRadius: 1, mt: 1 }}
        color={trainingUtilization > 90 ? 'error' : trainingUtilization > 75 ? 'warning' : 'success'}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {trainingUtilization}% utilized · {trainingRecords?.length ?? 0} training records
      </Typography>
    </Box>
  </Paper>
);

export const PIPsSection = ({ pips }: { pips: DRecord[] }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="PIPs" actionHref="/pips" actionLabel="View All" />
    <Grid container spacing={1}>
      {[
        { label: 'Active', value: pips?.filter((p: DRecord) => p.status === 'Active').length ?? 0, color: 'info' },
        { label: 'Review', value: pips?.filter((p: DRecord) => p.status === 'Review').length ?? 0, color: 'warning' },
        { label: 'Completed', value: pips?.filter((p: DRecord) => p.status === 'Completed').length ?? 0, color: 'success' },
        { label: 'Terminated', value: pips?.filter((p: DRecord) => p.status === 'Terminated').length ?? 0, color: 'error' },
      ].map(item => (
        <Grid size={{ xs: 6 }} key={item.label}>
          <Paper sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} color={`${item.color}.main`}>{item.value}</Typography>
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Paper>
);

export const TerminationsSection = ({ terminations }: { terminations: DRecord[] }) => (
  <Paper sx={{ p: 3 }}>
    <SectionHeader title="Terminations" actionHref="/terminations" actionLabel="View All" />
    <Grid container spacing={1}>
      {[
        { label: 'Pending Notice', value: terminations?.filter((t: DRecord) => t.status === 'PendingNotice').length ?? 0, color: 'warning' },
        { label: 'Pending Clearance', value: terminations?.filter((t: DRecord) => t.status === 'PendingClearance').length ?? 0, color: 'info' },
        { label: 'Completed', value: terminations?.filter((t: DRecord) => t.status === 'Completed').length ?? 0, color: 'success' },
      ].map(item => (
        <Grid size={{ xs: 6 }} key={item.label}>
          <Paper sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={700} color={`${item.color}.main`}>{item.value}</Typography>
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Paper>
);
